// Tags and filtering for the service board (issue #749).
//
// Tags are DERIVED from the case, never typed. A manual tagging system would
// mean extra work on every case and would go stale the moment someone forgot;
// reading the fields that are already there costs nothing and cannot disagree
// with the case itself.
//
// Two are shown, chosen from what the board actually holds rather than what
// might one day be useful: where the unit is, and whose it is. Warranty status,
// priority and the awaiting-customer flag are all unset across the live board,
// so tags for them would be permanently empty furniture.

const SUPPLIER_PREFIX = 'supplier:';

/** Where the unit physically is, or is being sent. */
export function locationTag(rma) {
  const raw = (rma && rma.assignedLocation) || '';
  if (!raw) return null;

  if (raw.startsWith(SUPPLIER_PREFIX)) {
    // A partner key is not for human eyes; the recipient pinned to the case at
    // assign time is the name the customer was actually given.
    const recipient = String((rma && rma.assignedAddress) || '').split('\n')[0].trim();
    const label = recipient || raw.slice(SUPPLIER_PREFIX.length).toUpperCase();
    return { key: raw, label, kind: 'partner' };
  }
  return { key: raw, label: raw, kind: 'internal' };
}

/** Whose unit it is. Blank on cases raised before the field was collected. */
export function customerTag(rma) {
  const org = ((rma && rma.contactOrg) || '').trim();
  if (!org) return null;
  return { key: org, label: org, kind: 'customer' };
}

/** A case raised to try the system out, not to service a real unit (#747). */
export function testTag(rma) {
  if (!rma || !rma.testInstance) return null;
  return { key: 'test', label: 'TEST', kind: 'test' };
}

export function tagsFor(rma) {
  // Test first: if a case is not real, that is the thing to notice about it.
  return [testTag(rma), locationTag(rma), customerTag(rma)].filter(Boolean);
}

/** The distinct values present on the board, so a filter never offers a dead option. */
export function filterOptions(rmas) {
  const locations = new Map();
  const customers = new Map();
  (rmas || []).forEach(rma => {
    const loc = locationTag(rma);
    if (loc) locations.set(loc.key, loc.label);
    const cust = customerTag(rma);
    if (cust) customers.set(cust.key, cust.label);
  });
  const sorted = map => [...map.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return { locations: sorted(locations), customers: sorted(customers) };
}

export const EMPTY_FILTERS = { location: '', customer: '', search: '' };

export function hasActiveFilter(filters) {
  const f = filters || EMPTY_FILTERS;
  return Boolean(f.location || f.customer || (f.search || '').trim());
}

/**
 * Does this case match the current filter?
 *
 * Search covers the three things someone reads off a label or an email: the RMA
 * number, the serial, and the customer. Every unit on a bundled case is searched,
 * not just the primary -- looking up a serial that is on the case but is not the
 * first one should still find it.
 */
export function matchesFilters(rma, filters) {
  const f = filters || EMPTY_FILTERS;

  if (f.location) {
    const loc = locationTag(rma);
    if (!loc || loc.key !== f.location) return false;
  }

  if (f.customer) {
    const cust = customerTag(rma);
    if (!cust || cust.key !== f.customer) return false;
  }

  const term = (f.search || '').trim().toLowerCase();
  if (term) {
    const devices = Array.isArray(rma.devices) ? rma.devices : [];
    const haystack = [
      rma.rmaId,
      rma.serialNumber,
      rma.contactOrg,
      rma.customerName,
      ...devices.map(d => d && d.serialNumber),
      ...devices.map(d => d && d.serviceTagNumber),
    ].filter(Boolean).join(' ').toLowerCase();
    if (!haystack.includes(term)) return false;
  }

  return true;
}

export function applyFilters(rmas, filters) {
  if (!hasActiveFilter(filters)) return rmas || [];
  return (rmas || []).filter(rma => matchesFilters(rma, filters));
}
